import { useState, useEffect } from 'react';
import { db } from '../db';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Users, ChevronRight, Check, X } from 'lucide-react';

const IOUCard = () => {
  const [friendTotals, setFriendTotals] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [friendIOUs, setFriendIOUs] = useState([]);

  useEffect(() => {
    loadIOUs();
  }, []);

  const loadIOUs = async () => {
    try {
      const allIOUs = await db.ious.where('paidBack').equals(0).toArray();
      // Group by friend name
      const totals = {};
      allIOUs.forEach(iou => {
        if (!totals[iou.friendName]) {
          totals[iou.friendName] = { name: iou.friendName, total: 0, currency: iou.currency, count: 0 };
        }
        totals[iou.friendName].total += iou.amount;
        totals[iou.friendName].count += 1;
      });
      setFriendTotals(Object.values(totals));
    } catch (e) {
      console.error('Failed to load IOUs:', e);
    }
  };

  const openFriendDetail = async (friendName) => {
    setSelectedFriend(friendName);
    const ious = await db.ious.where('friendName').equals(friendName).toArray();
    setFriendIOUs(ious.sort((a, b) => new Date(b.date) - new Date(a.date)));
  };

  const markAsPaid = async (iouId) => {
    await db.ious.update(iouId, { paidBack: 1 });
    // Reload
    const ious = await db.ious.where('friendName').equals(selectedFriend).toArray();
    setFriendIOUs(ious.sort((a, b) => new Date(b.date) - new Date(a.date)));
    loadIOUs();
  };

  const markAsUnpaid = async (iouId) => {
    await db.ious.update(iouId, { paidBack: 0 });
    const ious = await db.ious.where('friendName').equals(selectedFriend).toArray();
    setFriendIOUs(ious.sort((a, b) => new Date(b.date) - new Date(a.date)));
    loadIOUs();
  };

  if (friendTotals.length === 0) return null;

  const grandTotal = friendTotals.reduce((sum, f) => sum + f.total, 0);
  const mainCurrency = friendTotals[0]?.currency || 'USD';

  return (
    <>
      <Card
        data-testid="iou-card"
        className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/25 cursor-pointer hover:border-amber-500/40 transition-colors"
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-500" />
              <span className="font-semibold text-foreground">Friends Owe You</span>
            </div>
            <span className="text-lg font-bold text-amber-500">
              {grandTotal.toFixed(2)} {mainCurrency}
            </span>
          </div>
          <div className="space-y-2">
            {friendTotals.map((friend) => (
              <button
                key={friend.name}
                data-testid={`iou-friend-${friend.name.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => openFriendDetail(friend.name)}
                className="w-full flex items-center justify-between py-2 px-3 rounded-lg bg-card/50 hover:bg-card transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 text-xs font-bold">
                    {friend.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-foreground">{friend.name}</p>
                    <p className="text-xs text-muted-foreground">{friend.count} item{friend.count > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-semibold text-amber-500">{friend.total.toFixed(2)}</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Friend Detail Modal */}
      <Dialog open={!!selectedFriend} onOpenChange={() => setSelectedFriend(null)}>
        <DialogContent data-testid="iou-detail-modal" className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 text-sm font-bold">
                {selectedFriend?.charAt(0).toUpperCase()}
              </div>
              {selectedFriend}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {friendIOUs.map((iou) => (
              <div
                key={iou.id}
                className={`flex items-center justify-between p-3 rounded-lg ${iou.paidBack ? 'bg-green-500/10 border border-green-500/20' : 'bg-secondary/30'}`}
                data-testid={`iou-item-${iou.id}`}
              >
                <div className="flex-1">
                  <p className={`text-sm font-medium ${iou.paidBack ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                    {iou.note}
                  </p>
                  <p className="text-xs text-muted-foreground">{iou.date}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-semibold ${iou.paidBack ? 'text-green-500' : 'text-amber-500'}`}>
                    {iou.amount.toFixed(2)} {iou.currency}
                  </span>
                  {iou.paidBack ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => markAsUnpaid(iou.id)}
                      className="h-8 w-8 p-0 text-green-500 hover:text-red-500"
                      data-testid={`undo-paid-${iou.id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => markAsPaid(iou.id)}
                      className="h-8 px-3 bg-green-500 hover:bg-green-600 text-white rounded-full text-xs"
                      data-testid={`mark-paid-${iou.id}`}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Paid
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IOUCard;
